/**
 * Utilidades de validación para formularios de autenticación y perfil
 * Centraliza todas las validaciones para mantener consistencia
 */

/**
 * Valida si un email tiene formato válido
 * @param {string} email - Email a validar
 * @returns {boolean} - True si el email es válido
 */
export const isEmailValid = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // Expresión regular para verificar el formato de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Valida si una contraseña cumple los requisitos mínimos
 * @param {string} password - Contraseña a validar
 * @returns {object} - Objeto con isValid y mensaje de error
 */
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return {
      isValid: false,
      message: 'La contraseña es requerida'
    };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      message: 'La contraseña debe tener al menos 8 caracteres'
    };
  }

  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida si dos contraseñas coinciden
 * @param {string} password - Contraseña original
 * @param {string} confirmPassword - Confirmación de contraseña
 * @returns {object} - Objeto con isValid y mensaje de error
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  if (password !== confirmPassword) {
    return {
      isValid: false,
      message: 'Las contraseñas no coinciden'
    };
  }

  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida si un email es diferente al email actual del usuario
 * @param {string} newEmail - Nuevo email
 * @param {string} currentEmail - Email actual
 * @returns {object} - Objeto con isValid y mensaje de error
 */
export const validateEmailChange = (newEmail, currentEmail) => {
  if (!newEmail || !newEmail.trim()) {
    return {
      isValid: false,
      message: 'El nuevo email es requerido'
    };
  }

  if (!isEmailValid(newEmail)) {
    return {
      isValid: false,
      message: 'El nuevo email no tiene un formato válido'
    };
  }

  if (newEmail.trim().toLowerCase() === currentEmail?.toLowerCase()) {
    return {
      isValid: false,
      message: 'El nuevo email es igual al actual'
    };
  }

  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida el formulario completo de cambio de email
 * @param {string} newEmail - Nuevo email
 * @param {string} currentEmail - Email actual
 * @returns {object} - Objeto con isValid y mensaje de error
 */
export const validateEmailForm = (newEmail, currentEmail) => {
  return validateEmailChange(newEmail, currentEmail);
};

/**
 * Valida el formulario completo de cambio de contraseña
 * @param {string} newPassword - Nueva contraseña
 * @param {string} confirmPassword - Confirmación de contraseña
 * @returns {object} - Objeto con isValid y mensaje de error
 */
export const validatePasswordForm = (newPassword, confirmPassword) => {
  const passwordValidation = validatePassword(newPassword);
  if (!passwordValidation.isValid) {
    return passwordValidation;
  }

  return validatePasswordMatch(newPassword, confirmPassword);
};

/**
 * Genera un mensaje de error amigable basado en el error de Supabase
 * @param {object} error - Error de Supabase
 * @returns {string} - Mensaje de error amigable
 */
export const getAuthErrorMessage = (error) => {
  if (!error) return 'Error desconocido';

  // Errores comunes de Supabase Auth
  switch (error.message?.toLowerCase()) {
    case 'invalid login credentials':
      return 'Credenciales inválidas';
    case 'email already registered':
      return 'El email ya está registrado';
    case 'weak password':
      return 'La contraseña es muy débil';
    case 'password should be at least 6 characters':
      return 'La contraseña debe tener al menos 6 caracteres';
    case 'user not found':
      return 'Usuario no encontrado';
    case 'invalid email':
      return 'Email inválido';
    case 'session expired':
      return 'Sesión expirada';
    default:
      return error.message || 'Ha ocurrido un error inesperado';
  }
};
